<template>
  <div
    ref="navBody"
    class="navbody"
  >
    <div
      class="navList"
      @touchstart.stop.prevent="start"
      @touchmove.stop.prevent="move"
      @touchend.stop.prevent="end"
      @touchcancel.stop.prevent="end"
      @click.stop.prevent="clickItem"
    >
      <div
        v-for="item in navList"
        :key="item"
        :class="navClass(item)"
        :data-name="item"
      >
        {{ item }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { handleDomData, getIndex } from '../common/js/dom';

defineOptions({name: 'NavList'});

const props = defineProps({
  navList: {
    type: Array,
    default: () => []
  },
  flagText: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['toElement']);
const navBody = ref(null);
const touch = reactive({startIndex: '', endIndex: ''});
const activeItem = ref('');
const isTouch = ref(false);
const itemHeight = ref(24);
const paddingTop = ref(8);
let activeTimer = null;
let suppressClickUntil = 0;

watch(() => props.navList, updateMetrics);
onMounted(updateMetrics);
onBeforeUnmount(clearActiveTimer);

function clearActiveTimer () {
  if (activeTimer) {
    clearTimeout(activeTimer);
    activeTimer = null;
  }
}

function updateMetrics () {
  nextTick(() => {
    const firstItem = navBody.value && navBody.value.querySelector('[data-name]');
    if (firstItem) {
      itemHeight.value = firstItem.offsetHeight;
    }

    if (navBody.value) {
      const value = parseInt(getComputedStyle(navBody.value, false).paddingTop, 10);
      if (!Number.isNaN(value)) {
        paddingTop.value = value;
      }
    }
  });
}

function navClass (item) {
  const current = activeItem.value || (
    isTouch.value ? props.navList[touch.endIndex] : props.flagText
  );
  return current === item ? 'flagItem' : 'navItem';
}

function scrollToElement (item) {
  emit('toElement', item);
}

function setActiveItem (item) {
  activeItem.value = item;
  clearActiveTimer();
  activeTimer = setTimeout(() => {
    activeItem.value = '';
    activeTimer = null;
  }, 650);
}

function getTouchIndex (event) {
  const point = event.touches && event.touches.length
    ? event.touches[0]
    : event.changedTouches && event.changedTouches[0];

  if (!point || !navBody.value || !itemHeight.value) {
    return -1;
  }

  const rect = navBody.value.getBoundingClientRect();
  const index = Math.floor((point.clientY - rect.top - paddingTop.value) / itemHeight.value);
  return Math.min(Math.max(index, 0), props.navList.length - 1);
}

function start (event) {
  if (!props.navList.length) {
    return;
  }
  isTouch.value = true;
  touch.startIndex = getTouchIndex(event);
  if (touch.startIndex === -1) {
    touch.startIndex = '';
    return;
  }
  touch.endIndex = touch.startIndex;
  const item = props.navList[touch.startIndex];
  setActiveItem(item);
  scrollToElement(item);
}

function move (event) {
  if (!props.navList.length || touch.startIndex === '' || !itemHeight.value) {
    return;
  }
  isTouch.value = true;
  touch.endIndex = getTouchIndex(event);
  if (touch.endIndex < 0) {
    return;
  }
  const item = props.navList[touch.endIndex];
  setActiveItem(item);
  scrollToElement(item);
}

function clickItem (event) {
  if (Date.now() < suppressClickUntil) {
    return;
  }

  const item = handleDomData(event.target, 'data-name');
  if (!item || getIndex(props.navList, item) === -1) {
    return;
  }

  setActiveItem(item);
  scrollToElement(item);
}

function end () {
  suppressClickUntil = Date.now() + 500;
  isTouch.value = false;
}
</script>

<style lang="stylus" scoped>
.navbody
  width 44px
  border-radius 22px
  height auto
  position absolute
  top calc(50% + 32px)
  right 0
  transform translateY(-50%)
  z-index 95
  box-sizing border-box
  padding 8px 0px
  touch-action none
  .navItem
    width 44px
    height 24px
    text-align center
    line-height 24px
    font-size 16px
    color var(--text-muted)
    user-select none
  .flagItem
    width 44px
    height 24px
    margin 0
    text-align center
    line-height 24px
    font-size 16px
    color var(--on-accent-color)
    background var(--selection-color)
    border-radius 12px
    user-select none
</style>
